// src/services/api.js
import axios from 'axios';
import { toast } from 'react-toastify';
import { ErrorPageStatus, navigationService } from './navigationService';
import CloudWatchLogger from 'services/cloudwatch/cloudwatchLogger';

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8080',
    // Ensure cookies are sent with cross-origin requests
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Navigation helper function
const navigateToError = (errorCode: ErrorPageStatus, errorDetails: Record<string, any> = {}) => {
    navigationService.navigateToError(errorCode, errorDetails);
};

// Helper function to get the CloudWatch logger instance
const getLogger = () => {
    return CloudWatchLogger.getInstance();
};

// Response interceptor for successful responses
api.interceptors.response.use((response) => {
    // Log successful API responses (optional - can be enabled for debugging)
    const logger = getLogger();
    logger?.info(
        `API request successful: ${response.config?.method?.toUpperCase()} ${response.config?.url}`,
        {
            status: response.status,
            statusText: response.statusText,
            url: response.config?.url,
            method: response.config?.method,
            timestamp: new Date().toISOString(),
        },
        'API',
        'success'
    );
    return response;
});

// Error interceptor for handling API errors
api.interceptors.response.use(
    null, // No success handler - let the response interceptor handle success
    (error) => {
        const logger = getLogger();
        const errorDetails = {
            status: error.response?.status,
            statusText: error.response?.statusText,
            url: error.config?.url,
            method: error.config?.method,
            data: error.response?.data,
            timestamp: new Date().toISOString(),
        };

        // Log all API errors with consistent approach
        logger?.error(
            `API error: ${error.response?.status || 'Network'} ${error.response?.statusText || 'Error'}`,
            error,
            errorDetails,
            'API',
            'error'
        );

        // Check if toast notification should be suppressed
        const suppressToast = error.config?.suppressErrorToast;

        // Handle specific error cases for user experience
        if (error.response?.status === 401) {
            // Clear token and redirect to login if unauthorized
            window.location.href = '/login';
        } else if (error.response?.status === 502 || error.response?.status === 503) {
            // Navigate to error page with detailed error information
            navigateToError(error.response.status, {
                data: error.response?.data,
                config: error.config,
            });
        } else if (error.response?.status >= 400 && error.response?.status <= 500) {
            const errorMessage = error.response?.data?.message || error.response?.statusText;
            const errorDetails = error.response?.data?.error || error.response?.status;
            if (!suppressToast) toast.error(`Something went wrong: ${errorMessage} ${errorDetails}`);
        }
        return Promise.reject(error);
    }
);

export default api;
